import { useQuery } from "@tanstack/react-query";
import React from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Alert, Row } from "reactstrap";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import LoadingContent from "../loading-content";
import CreditCards from "../credit-cards";
import CreditTransactions from "../credit-transactions";
import CreditUsageTable from "../usage-table";

/**
 * Single credit-pool overview used by providers like D&B, Handelsregister and AI.
 * `feature` is the flag the provider depends on; pass null when it has none.
 */
const ProviderOverview = ({ t, provider, feature = "kyb_activation", showUsageTable = true }) => {
    const isEnabled = !feature || isFeatureEnabledForActiveModule(feature);
    const {
        data: overviewData,
        isFetching: isFetchingOverview,
    } = useQuery({
        queryKey: [
            "3rd-party-management-fetch-organization-license-overview",
            provider,
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchkYBLicenseDetail({
                provider,
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: isEnabled,
        onError: () => {
            toast(t("An error occurred while fetching overview content."), {
                type: "error",
            });
        },
    });

    if (!isEnabled) {
        return (
            <Alert color="warning">
                <p className="mb-0">
                    {t("Please upgrade your plan to access this feature")}.
                </p>
            </Alert>
        );
    }

    if (isFetchingOverview) {
        return <LoadingContent />;
    }

    const { usageCounts = {}, totalCredits = 0, creditTransactions = [] } =
        overviewData || {};

    let totalUsage = 0;

    Object.keys(usageCounts).forEach((usageKey) => {
        totalUsage += usageCounts[usageKey].usageCount || 0;
    });

    const remaining = totalCredits - totalUsage;

    return (
        <div className="overview-tab">
            <Row>
                <CreditCards
                    totalCredits={totalCredits}
                    totalUsage={totalUsage}
                    remaining={remaining}
                />
                {showUsageTable ? (
                    <CreditUsageTable provider={provider} />
                ) : null}
                <CreditTransactions transactions={creditTransactions} />
            </Row>
        </div>
    );
};

export default withNamespaces()(ProviderOverview);
